import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Cookie-based auth: send the httpOnly session cookie with every request.
const api = axios.create({ baseURL: API, withCredentials: true });

function readCookie(name) {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]) : null;
}

const SAFE_METHODS = ["get", "head", "options"];

// Enhancer referral: remembered for 30 days from their profile or a ?ref=<enhancerId> link.
const REF_KEY = "eyw_ref";
export const rememberReferral = (id) => { if (id) try { localStorage.setItem(REF_KEY, JSON.stringify({ id, at: Date.now() })); } catch { /* ignore */ } };
const currentReferral = () => {
  try { const r = JSON.parse(localStorage.getItem(REF_KEY) || "null"); return r && Date.now() - r.at < 30 * 864e5 ? r.id : ""; } catch { return ""; }
};
if (typeof window !== "undefined") rememberReferral(new URLSearchParams(window.location.search).get("ref"));

// Double-submit CSRF: echo the readable csrf_token cookie as a header on mutations.
api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method === "post" && config.url === "/bookings" && config.data && typeof config.data === "object" && !config.data.referral_enhancer_id) {
    const ref = currentReferral();
    if (ref) config.data = { ...config.data, referral_enhancer_id: ref };
  }
  if (!SAFE_METHODS.includes(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

// Silent session renewal: on a 401, try one refresh (short-lived access cookie →
// renewed via the long-lived refresh cookie), then replay the original request once.
let refreshing = null;
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const skip = url.includes("/auth/refresh") || url.includes("/auth/login") ||
      url.includes("/auth/logout") || url.includes("/auth/me");
    // Only attempt a silent refresh when a session actually exists (csrf_token is set
    // alongside the httpOnly session cookie). Logged-out visitors skip the retry entirely,
    // so guest pages don't emit a second 401 from /auth/refresh.
    const hasSession = !!readCookie("csrf_token");
    if (status === 401 && original && !original._retry && !skip && hasSession) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "We hit a snag on our side. Please try again in a moment.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}

export default api;
